import React, { useContext, useMemo } from 'react';
import '@d3fc/d3fc-element';
import AppContext from 'app/AppContext';
import TimeSeriesChart from 'utils/components/TimeSeriesChart/TimeSeriesChart';
import {
    useYScale,
    useBpsFormat,
} from 'utils/components/TimeSeriesChart/yAxis';
import {
    FillsChartType,
    PnLConfig,
} from 'widgets/fillsGraph/store/configuration/FillsGraphConfiguration';
import { FillsGraphData } from 'widgets/fillsGraph/fillsGraphData';
import PLChartLegend from './PLChartLegend';
import './PLChart.scss';
import { deriveEstCostLabelProps } from './estCostLabel';
import styleConstants from 'widgets/fillsGraph/variables.module.scss';
import YAxisLabel from 'widgets/fillsGraph/axisLabel/YAxisLabel';
import { useShowCollapsableCompanionChart } from 'widgets/fillsGraph/collapsableCompanionCharts/useShowCollapsableCompanionChart';
import { PnlBenchmark } from 'utils/PnL';
import { FillsGraphPortfolioData } from 'widgets/fillsGraph/fillsGraphPortfolioData';
import { computeYDomain, getChartSeries, getHideSeriesClasses } from './series';
import { getFillsGraphPlData } from 'widgets/fillsGraph/pl/fillsGraphPlData';
import classNames from 'classnames';

const XAXIS_HEIGHT = parseInt(styleConstants.xAxisHeight);

export interface PLChartProps {
    width: number;
    height: number;
    chartHeight: number;
    fillsGraphData: FillsGraphData;
    fillsGraphPortfolioData: FillsGraphPortfolioData;
    fillsGraphContainerLeft: number;
    config: PnLConfig;
    displayXAxis: boolean;
}

export default function PLChart(props: PLChartProps) {
    const {
        width,
        height,
        chartHeight,
        fillsGraphData,
        fillsGraphPortfolioData,
        config,
        displayXAxis,
        fillsGraphContainerLeft,
    } = props;
    const { impactCost } = fillsGraphData;
    const { pnlChartBenchmark } = config;

    const renderCompanionChart = useShowCollapsableCompanionChart(
        FillsChartType.PnL
    );

    const { numberFormat } = useContext(AppContext);

    const yTicks = 3;

    const plData = getFillsGraphPlData(fillsGraphData);

    const series = getChartSeries(plData, impactCost);
    const yDomain = computeYDomain(config, plData);
    const yScale = useYScale(yDomain, [-1, +1]);
    const hideSeriesClasses = getHideSeriesClasses(config);

    const bpsFormat = useBpsFormat(numberFormat);

    const estCostLabelProps = deriveEstCostLabelProps(
        impactCost,
        numberFormat,
        yScale,
        chartHeight - XAXIS_HEIGHT,
        width
    );

    const cssClasses = classNames(
        `PLChart companion-chart`,
        hideSeriesClasses,
        !displayXAxis && 'hide-x-axis'
    );
    const style = useMemo(() => ({ height }), [height]);

    return (
        <div className={cssClasses} style={style}>
            <PLChartLegend
                fillsGraphData={fillsGraphData}
                plData={plData}
                fillsGraphPortfolioData={fillsGraphPortfolioData}
                pnlChartBenchmark={pnlChartBenchmark}
                fillsGraphContainerLeft={fillsGraphContainerLeft}
            />
            {renderCompanionChart && (
                <div className="chart-container">
                    {estCostLabelProps &&
                        pnlChartBenchmark === PnlBenchmark.Arrival && (
                            <YAxisLabel {...estCostLabelProps} />
                        )}
                    <TimeSeriesChart
                        height={chartHeight}
                        id="PLChart"
                        data={plData}
                        series={series}
                        displayXAxis={displayXAxis}
                        yScale={yScale}
                        yTicks={yTicks}
                        yTickFormat={bpsFormat}
                    />
                </div>
            )}
        </div>
    );
}
